import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axiosInstance from '../../../axiosInstance';
import SharedProfile from '../../common/Profile/SharedProfile';
import {
  getCurrentUser,
  checkPrivilegeOnResource,
} from '../../../services/authService';
import { RESOURCES } from '../../../constants/resourceConstants';
import { PRIVILEGES } from '../../../constants/privilege.constant';
import { formatPhoneForStorage } from '../../../utils/phoneUtils';
import useRolesApi from '../../../hooks/api/roles/useRolesApi';
import useUserPrivilegesApi from '../../../hooks/api/users/useUserPrivilegesApi';
import { ROLE_STATUS } from '../../../constants/role.constant';

const AdminProfile = () => {
  const { t } = useTranslation();
  const { id: paramId } = useParams();
  const [me, setMe] = useState(null);
  const [userData, setUserData] = useState(null);
  const [internalRoles, setInternalRoles] = useState([]);
  const [userPrivileges, setUserPrivileges] = useState(null);
  const [hasCustomPrivileges, setHasCustomPrivileges] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  const { getInternalRoles } = useRolesApi();
  const {
    updateUserPrivileges,
    getGrantedPrivileges,
    hasCustomPrivileges: checkHasCustomPrivileges,
  } = useUserPrivilegesApi();

  const isSelf =
    !paramId || (me && userData && String(me.id) === String(userData.id));

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const access = await checkPrivilegeOnResource(
          PRIVILEGES.EDIT,
          RESOURCES.ADMIN_USERS
        );
        setHasAdminAccess(access);
      } catch {
        setHasAdminAccess(false);
      }
    };
    checkAccess();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const meRaw = await getCurrentUser();
        const meUser = meRaw?.user || meRaw;
        setMe(meUser);

        const viewingSelf = !paramId || String(meUser.id) === String(paramId);

        const targetId = viewingSelf ? meUser.id : paramId;

        const privilegesFetch = Promise.all([
          getGrantedPrivileges(targetId),
          checkHasCustomPrivileges(targetId),
        ]);

        if (viewingSelf) {
          const [grantedPrivileges, customPriv] = await privilegesFetch;
          setUserData(meUser);
          setUserPrivileges(grantedPrivileges);
          setHasCustomPrivileges(customPriv);
        } else {
          const [[grantedPrivileges, customPriv], otherRes, rolesData] =
            await Promise.all([
              privilegesFetch,
              axiosInstance.get(`/api/users/${paramId}`, {
                withCredentials: true,
              }),
              getInternalRoles(),
            ]);
          setUserData(otherRes.data);
          setInternalRoles(
            rolesData.filter(
              (role) => role.status === ROLE_STATUS.PUBLISHED_ACTIVE
            )
          );
          setUserPrivileges(grantedPrivileges);
          setHasCustomPrivileges(customPriv);
        }
      } catch {
        toast.error(t('profileData.updateError'));
      }
    };
    fetchData();
  }, [
    paramId,
    t,
    getInternalRoles,
    getGrantedPrivileges,
    checkHasCustomPrivileges,
  ]);

  const canEdit = isSelf || hasAdminAccess;

  const handleSave = useCallback(
    async (formData) => {
      const url = isSelf ? '/api/users/me' : `/api/users/${paramId}`;
      const payload = {
        username: formData.username,
        ...(formData.phone
          ? { phone: formatPhoneForStorage(formData.phone) }
          : {}),
        ...(formData.role
          ? { role_id: formData.role } // eslint-disable-line camelcase
          : {}),
      };

      await axiosInstance.put(url, payload, { withCredentials: true });

      const refreshUserData = async () => {
        if (isSelf) {
          const meRaw = await getCurrentUser();
          const meUser = meRaw?.user || meRaw;
          setUserData(meUser);
          window.dispatchEvent(new Event('profile-updated'));
        } else if (paramId) {
          const res = await axiosInstance.get(`/api/users/${paramId}`, {
            withCredentials: true,
          });
          setUserData(res.data);
        }
      };

      await refreshUserData();
    },
    [isSelf, paramId]
  );

  const handleVerifyPassword = useCallback(async (currentPassword) => {
    await axiosInstance.post(
      '/api/users/me/password/verify',
      { currentPassword },
      { withCredentials: true }
    );
  }, []);

  const handleChangePassword = useCallback(
    async (currentPassword, newPassword) => {
      await axiosInstance.post(
        '/api/users/me/password/change',
        { currentPassword, newPassword },
        { withCredentials: true }
      );
    },
    []
  );

  const handleSaveRoleAndPrivileges = useCallback(
    async ({ roleId, privileges, roleChanged, privilegesChanged }) => {
      try {
        if (roleChanged) {
          await axiosInstance.put(
            `/api/users/${paramId}`,
            { role_id: roleId }, // eslint-disable-line camelcase
            { withCredentials: true }
          );
        }

        if (privilegesChanged) {
          await updateUserPrivileges(paramId, privileges);
        }

        toast.success(t('privilegesSection.saveSuccess'));

        const otherRes = await axiosInstance.get(`/api/users/${paramId}`, {
          withCredentials: true,
        });
        setUserData(otherRes.data);
      } catch (e) {
        const errorMsg =
          e.response?.data?.message || t('privilegesSection.saveError');
        toast.error(errorMsg);
        throw e;
      }
    },
    [paramId, updateUserPrivileges, t]
  );

  const personalFields = [
    {
      name: 'username',
      type: 'text',
      label: t('profileData.userName'),
      required: true,
      fullWidth: true,
    },
  ];

  const contactFields = [
    {
      name: 'email',
      type: 'email',
      label: t('Email'),
      required: true,
      fullWidth: true,
      disabled: true,
    },
    {
      name: 'phone',
      type: 'phoneWithCountry',
      label: t('profileData.phoneNumber'),
      required: true,
      fullWidth: true,
    },
  ];

  if (!userData) return null;

  return (
    <SharedProfile
      userData={userData}
      canEditProfile={canEdit}
      showPasswordTab={isSelf}
      showRolesTab={true}
      onSave={canEdit ? handleSave : undefined}
      onVerifyPassword={isSelf ? handleVerifyPassword : undefined}
      onChangePassword={isSelf ? handleChangePassword : undefined}
      personalFields={personalFields}
      contactFields={contactFields}
      showPrivilegesEditor={!isSelf && hasAdminAccess}
      roleOptions={internalRoles}
      onSaveRoleAndPrivileges={handleSaveRoleAndPrivileges}
      isInternal={true}
      userPrivileges={userPrivileges}
      hasCustomPrivileges={hasCustomPrivileges}
    />
  );
};

export default AdminProfile;
